import { AddressFields } from './AddressFields';

interface CustomerAddress {
  streetAddress: string;
  unit: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
}

interface ProjectSiteSectionProps {
  useSameAsCustomerAddress: boolean;
  siteStreetAddress: string;
  siteUnit: string;
  siteCity: string;
  siteProvince: string;
  sitePostalCode: string;
  siteCountry: string;
  primaryCustomerAddress: CustomerAddress | null;
  onChange: (field: string, value: string | boolean) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export function ProjectSiteSection({
  useSameAsCustomerAddress,
  siteStreetAddress,
  siteUnit,
  siteCity,
  siteProvince,
  sitePostalCode,
  siteCountry,
  primaryCustomerAddress,
  onChange,
  errors = {},
  disabled = false,
}: ProjectSiteSectionProps) {
  const handleToggleChange = (checked: boolean) => {
    onChange('useSameAsCustomerAddress', checked);

    // If toggling back to use customer address, clear site fields
    if (checked) {
      onChange('siteStreetAddress', '');
      onChange('siteUnit', '');
      onChange('siteCity', '');
      onChange('siteProvince', 'ON');
      onChange('sitePostalCode', '');
      onChange('siteCountry', 'Canada');
    }
  };

  // Format address for display
  const formatAddress = (addr: CustomerAddress | null): string => {
    if (!addr) return 'No address available';

    const parts = [
      addr.streetAddress,
      addr.unit,
      addr.city,
      addr.province,
      addr.postalCode,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'No address available';
  };

  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-6">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-secondary-900">
            Project Location
          </h2>
          <p className="text-sm text-secondary-500">
            Where the work will be performed
          </p>
        </div>
      </div>

      {/* Toggle */}
      <div className="mb-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={useSameAsCustomerAddress}
              onChange={(e) => handleToggleChange(e.target.checked)}
              className="sr-only"
              disabled={disabled}
            />
            <div
              className={`w-10 h-6 rounded-full transition-colors ${
                useSameAsCustomerAddress ? 'bg-primary-500' : 'bg-secondary-300'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div
                className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform mt-1 ${
                  useSameAsCustomerAddress ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </div>
          </div>
          <span className="text-sm font-medium text-secondary-700">
            Site location is same as customer's billing address
          </span>
        </label>
      </div>

      {/* Customer Address Display */}
      {useSameAsCustomerAddress && (
        <div className="p-4 bg-secondary-50 rounded-lg">
          <p className="text-sm text-secondary-600 mb-1">Using address:</p>
          <p className="text-sm font-medium text-secondary-900">
            {formatAddress(primaryCustomerAddress)}
          </p>
          {!primaryCustomerAddress?.streetAddress && (
            <p className="text-xs text-amber-600 mt-2">
              The primary customer doesn't have a complete address. Consider entering a site location.
            </p>
          )}
        </div>
      )}

      {/* Custom Site Address Fields */}
      {!useSameAsCustomerAddress && (
        <div className="mt-4">
          <AddressFields
            streetAddress={siteStreetAddress}
            unit={siteUnit}
            city={siteCity}
            province={siteProvince}
            postalCode={sitePostalCode}
            country={siteCountry}
            onChange={onChange}
            errors={{
              streetAddress: errors.siteStreetAddress,
              unit: errors.siteUnit,
              city: errors.siteCity,
              province: errors.siteProvince,
              postalCode: errors.sitePostalCode,
              country: errors.siteCountry,
            }}
            disabled={disabled}
            fieldPrefix="site"
            requiredFields={{
              streetAddress: true,
              city: true,
              province: true,
              postalCode: true,
              country: true,
            }}
          />
        </div>
      )}
    </div>
  );
}
